/**
 * react-dropzone requires its `accept` option as a Record<MIME, extensions[]>,
 * but the W3C standard `accept` attribute string is what most consumers and
 * design specs already use. This helper normalises either shape into the
 * Record form so the primitives can accept both at the API surface.
 *
 * Conversion rules for the string form (comma-separated tokens, e.g.
 * ".pdf,.doc,application/pdf,image/*"):
 *   - Tokens containing "/" are treated as MIME types and become keys.
 *   - Tokens starting with "." are treated as file extensions and become
 *     values. Every MIME key gets the full extension list (react-dropzone's
 *     matcher treats keys and values as OR-clauses).
 *   - If the string contains only extensions, they are bucketed under an
 *     empty MIME key. react-dropzone's `acceptPropAsAcceptAttr` discards
 *     entries whose key is neither a valid MIME nor an extension, so the
 *     rendered `<input accept>` attribute ends up extension-only. The empty
 *     key also can't accidentally match real files via the MIME-equality
 *     short-circuit in
 *     `attr-accept` or our paste matcher (both guard `file.type && ...`),
 *     unlike `application/octet-stream` which would let zips, exes, and
 *     other unknown-binary uploads through regardless of extension.
 *   - Unknown tokens (no "/" and no leading ".") are dropped silently.
 */

const ORPHAN_EXTENSION_BUCKET = '';

export type NormalizedAccept = ReturnType<typeof normalizeAccept>;

export function normalizeAccept(
  accept: string | Record<string, readonly string[]>
): Record<string, string[]> {
  if (typeof accept !== 'string') {
    return Object.fromEntries(
      Object.entries(accept).map(([mime, exts]) => [mime, [...exts]])
    );
  }

  const tokens = accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const mimes = tokens.filter((t) => t.includes('/'));
  const extensions = tokens.filter((t) => t.startsWith('.'));

  const result: Record<string, string[]> = {};

  if (mimes.length > 0) {
    for (const mime of mimes) {
      result[mime] = [...extensions];
    }
  } else if (extensions.length > 0) {
    result[ORPHAN_EXTENSION_BUCKET] = [...extensions];
  }

  return result;
}
